import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import RoleSelector from './RoleSelector';
import { Mail, Link2, Copy, Check, RefreshCw, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function InviteMemberDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
  currentUserRole,
  onInviteSent,
}) {
  const [activeTab, setActiveTab] = useState('email');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [sending, setSending] = useState(false);
  const [linkRole, setLinkRole] = useState('viewer');
  const [generatedLink, setGeneratedLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);

  const loadPendingInvites = async () => {
    setLoadingInvites(true);
    try {
      const response = await api.get(`/projects/${projectId}/invitations`);
      setPendingInvites(response.data);
    } catch (error) {
      console.error('Failed to load invitations:', error);
    } finally {
      setLoadingInvites(false);
    }
  };

  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      loadPendingInvites();
    } else {
      // Reset state when closing
      setEmail('');
      setRole('viewer');
      setGeneratedLink(null);
      setCopied(false);
    }
    onOpenChange(isOpen);
  };

  const handleSendEmailInvite = async (e) => {
    e.preventDefault();
    if (!email) return;

    setSending(true);
    try {
      await api.post(`/projects/${projectId}/invitations/email`, {
        email,
        role,
      });
      toast.success(`Invitation sent to ${email}`);
      setEmail('');
      setRole('viewer');
      loadPendingInvites();
      if (onInviteSent) onInviteSent();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send invitation';
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const handleGenerateLink = async () => {
    setGeneratingLink(true);
    try {
      const response = await api.post(`/projects/${projectId}/invitations/link`, {
        role: linkRole,
        max_uses: 10, // Allow up to 10 uses by default
      });
      setGeneratedLink(response.data);
      loadPendingInvites();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate link';
      toast.error(message);
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyLink = async () => {
    if (!generatedLink?.invite_link) return;

    try {
      await navigator.clipboard.writeText(generatedLink.invite_link);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleRevokeInvite = async (invitationId) => {
    try {
      await api.delete(`/invitations/${invitationId}`);
      toast.success('Invitation revoked');
      loadPendingInvites();
      if (generatedLink?.id === invitationId) {
        setGeneratedLink(null);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to revoke invitation';
      toast.error(message);
    }
  };

  // Only show roles the current user can assign
  const maxRole = currentUserRole === 'owner' ? 'owner' : 'editor';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Invite to {projectName}</DialogTitle>
          <DialogDescription>
            Add team members to collaborate on this project
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="email">
              <Mail className="h-4 w-4 mr-2" />
              Email Invite
            </TabsTrigger>
            <TabsTrigger value="link">
              <Link2 className="h-4 w-4 mr-2" />
              Invite Link
            </TabsTrigger>
          </TabsList>

          <TabsContent value="email" className="space-y-4 mt-4">
            <form onSubmit={handleSendEmailInvite} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="colleague@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <RoleSelector
                  value={role}
                  onChange={setRole}
                  excludeOwner={currentUserRole !== 'owner'}
                  maxRole={maxRole}
                />
              </div>

              <Button type="submit" className="w-full" disabled={sending || !email}>
                {sending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="link" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Role for invited members</Label>
              <RoleSelector
                value={linkRole}
                onChange={setLinkRole}
                excludeOwner={currentUserRole !== 'owner'}
                maxRole={maxRole}
              />
            </div>

            {!generatedLink ? (
              <Button
                onClick={handleGenerateLink}
                className="w-full"
                disabled={generatingLink}
              >
                {generatingLink ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Link2 className="h-4 w-4 mr-2" />
                    Generate Invite Link
                  </>
                )}
              </Button>
            ) : (
              <Card>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Input
                        readOnly
                        value={generatedLink.invite_link}
                        className="font-mono text-sm"
                      />
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={handleCopyLink}
                      >
                        {copied ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>
                        Uses: {generatedLink.use_count}/{generatedLink.max_uses || 'unlimited'}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleRevokeInvite(generatedLink.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Revoke
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <p className="text-sm text-muted-foreground">
              Anyone with this link can join the project with the selected role.
            </p>
          </TabsContent>
        </Tabs>

        {/* Pending Invitations */}
        {pendingInvites.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <Label className="text-sm text-muted-foreground">
              Pending Invitations ({pendingInvites.length})
            </Label>
            <div className="mt-2 space-y-2 max-h-[150px] overflow-y-auto">
              {pendingInvites.map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between p-2 rounded-md bg-muted/50"
                >
                  <div className="flex items-center gap-2">
                    {invite.invite_type === 'email' ? (
                      <Mail className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Link2 className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="text-sm">
                      {invite.email || 'Invite Link'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ({invite.role})
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => handleRevokeInvite(invite.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
